import React, { useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Checkbox, Empty, Input, Select } from "antd"
import { SearchOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import { Icon } from "ui/Icon"
import FilterChips from "ui/FilterChips"
import { includesId } from "helpers/blackouts"
import { areAllChildrenSelected, areSomeChildrenSelected, buildSelectedChips, collectSelectableIds, formatOptionLabel } from "../utils"
import styles from "./BlackoutTargetSelector.module.css"

/**
 * Multi-select for the three blackout target kinds.
 *
 * Existing pickers (`Equipment/experience/ExperienceSelector`, `ui/MultipleSelect`) are all
 * flat, but equipment needs selectable *parents*: checking "Kayak · 5 assets" selects all
 * five assets, and checking three of five leaves the parent indeterminate. Only leaf ids
 * are ever emitted, so callers never deal with the nesting.
 */
const BlackoutTargetSelector = ({ options, value, onChange, placeholder, showAllOption, isLoading }) => {
  const { t } = useTranslation()
  const [searchText, setSearchText] = useState("")

  const normalizedSearch = searchText.trim().toLowerCase()

  const matchesSearch = (option) => formatOptionLabel(option).toLowerCase().includes(normalizedSearch)

  // A parent stays visible when it matches, or when any of its children match.
  const filteredOptions = useMemo(() => {
    if (!normalizedSearch) return options
    return options.reduce((visible, option) => {
      if (matchesSearch(option)) return [...visible, option]
      const matchingChildren = option.children?.filter(matchesSearch) || []
      if (matchingChildren.length) return [...visible, { ...option, children: matchingChildren }]
      return visible
    }, [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, normalizedSearch])

  const allSelectableIds = useMemo(() => collectSelectableIds(options), [options])
  const areAllSelected = allSelectableIds.length > 0 && allSelectableIds.every((id) => includesId(value, id))

  const chips = useMemo(() => buildSelectedChips(options, value), [options, value])

  /**
   * `FilterChips` works on a flat list of values, but a chip here can stand for a whole parent
   * ("Kayak · 5 assets"). Chip ids are what the list is keyed on; removing one expands back to
   * the leaf ids it covers, since those are all `value` ever holds.
   */
  const chipOptions = useMemo(() => chips.map((chip) => ({ value: chip.id, label: chip.label })), [chips])
  const chipValues = useMemo(() => chips.map((chip) => chip.id), [chips])

  const handleChipsChange = (remainingChipIds) => {
    const keptIds = chips.filter((chip) => includesId(remainingChipIds, chip.id)).flatMap((chip) => chip.childIds || [chip.id])
    onChange(value.filter((id) => includesId(keptIds, id)))
  }

  const toggleAll = (isChecked) => onChange(isChecked ? allSelectableIds : [])

  const toggleOption = (option, isChecked) => {
    const ids = option.children?.length ? option.children.map((child) => child.id) : [option.id]
    if (isChecked) onChange([...new Set([...value, ...ids])])
    else onChange(value.filter((id) => !includesId(ids, id)))
  }

  const renderRow = (option, isChild = false) => {
    const hasChildren = Boolean(option.children?.length)
    const isChecked = hasChildren ? areAllChildrenSelected(option, value) : includesId(value, option.id)
    const isIndeterminate = hasChildren && areSomeChildrenSelected(option, value)

    return (
      <React.Fragment key={option.id}>
        <button
          type="button"
          className={`${styles.optionRow} ${isChild ? styles.childRow : ""}`}
          aria-pressed={isChecked}
          onClick={(event) => {
            event.stopPropagation()
            toggleOption(option, !isChecked)
          }}
        >
          <Checkbox checked={isChecked} indeterminate={isIndeterminate} className={styles.checkbox} />
          <Text variant="caption" size="lg" className={styles.optionLabel}>
            {option.label}
            {option.suffix && <span className={styles.optionSuffix}>{option.suffix}</span>}
          </Text>
        </button>
        {hasChildren && option.children.map((child) => renderRow(child, true))}
      </React.Fragment>
    )
  }

  const dropdownRender = () => (
    <section className={styles.dropdown} onClick={(event) => event.stopPropagation()}>
      <div className={styles.searchWrapper}>
        <Input
          allowClear
          placeholder={t("settings.blackout.search", { defaultValue: "Search" })}
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        />
      </div>

      <div className={styles.optionList}>
        {showAllOption && options.length > 0 && !normalizedSearch && (
          <button
            type="button"
            className={styles.optionRow}
            aria-pressed={areAllSelected}
            onClick={(event) => {
              event.stopPropagation()
              toggleAll(!areAllSelected)
            }}
          >
            <Checkbox checked={areAllSelected} className={styles.checkbox} />
            <Text variant="caption" size="lg" className={styles.optionLabel}>
              {t("settings.blackout.select_all", { defaultValue: "All" })}
            </Text>
          </button>
        )}

        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => renderRow(option))
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("settings.blackout.no_results", { defaultValue: "No results found" })} />
        )}
      </div>
    </section>
  )

  return (
    <div className={styles.wrapper}>
      <Select
        className={styles.select}
        placeholder={placeholder}
        value={[]}
        mode="multiple"
        showSearch={false}
        loading={isLoading}
        dropdownRender={dropdownRender}
        suffixIcon={<Icon name="ChevronDown" width={16} height={16} />}
        maxTagCount={0}
        maxTagPlaceholder={() => null}
        tagRender={() => null}
      />

      <FilterChips selectedValues={chipValues} options={chipOptions} onChange={handleChipsChange} className={styles.chipList} />
    </div>
  )
}

BlackoutTargetSelector.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      // Ids may serialize as string or number depending on the endpoint — see `isSameId`.
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      suffix: PropTypes.string,
      children: PropTypes.array
    })
  ).isRequired,
  value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  showAllOption: PropTypes.bool,
  isLoading: PropTypes.bool
}

export default BlackoutTargetSelector
